import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import { Autocomplete, TextField } from "@mui/material";
import {
  Button,
  Spinner,
  Alert,
  Container,
  Table,
  Modal,
} from "react-bootstrap";
import {
  FiUsers,
  FiArrowLeft,
  FiEdit2,
  FiTrash2,
  FiSave,
  FiXCircle,
} from "react-icons/fi";

import "./ManageUsers.css";

const BULK_ELIGIBLE_TITLES = [
  "Zonal Heads",
  "Branch Managers",
  "Team Leads",
  "Relationship Managers",
];

const createInitialBulkSelectionState = () =>
  BULK_ELIGIBLE_TITLES.reduce((acc, title) => {
    acc[title] = [];
    return acc;
  }, {});

const createInitialBulkModalState = () => ({
  roleTitle: "",
  supervisorUsername: "",
});

const ManageUsers = () => {
  const navigate = useNavigate();
  const [locationFilter, setLocationFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [users, setUsers] = useState({
    directors: [],
    zonalHeads: [],
    branchManagers: [],
    teamLeads: [],
    relationshipManagers: [],
  });

  const [dropdownData, setDropdownData] = useState({
    directors: [],
    zonalHeads: [],
    branchManagers: [],
    teamLeads: [],
  });

  const [editingUser, setEditingUser] = useState(null);
  const [editingRoleUser, setEditingRoleUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [selectedRole, setSelectedRole] = useState("");
  const [saving, setSaving] = useState(false);

  // Confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditConfirmModal, setShowEditConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [deleteRole, setDeleteRole] = useState("");
  const [editRole, setEditRole] = useState("");

  // For RM and Team Lead cascading dropdowns
  const [selectedLocation, setSelectedLocation] = useState("");
  const [availableLocations, setAvailableLocations] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // For Team Lead cascading dropdown
  const [tlSelectedLocation, setTlSelectedLocation] = useState("");
  const [tlAvailableLocations, setTlAvailableLocations] = useState([]);
  const [tlAvailableUsers, setTlAvailableUsers] = useState([]);
  const [tlSelectedUser, setTlSelectedUser] = useState(null);

  // For Branch Manager cascading dropdown
  const [bmSelectedLocation, setBmSelectedLocation] = useState("");
  const [bmAvailableLocations, setBmAvailableLocations] = useState([]);
  const [bmAvailableUsers, setBmAvailableUsers] = useState([]);
  const [bmSelectedUser, setBmSelectedUser] = useState(null);

  const [selectedBulkUsers, setSelectedBulkUsers] = useState(
    createInitialBulkSelectionState
  );
  const [bulkModalState, setBulkModalState] = useState(
    createInitialBulkModalState
  );
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Admin info for email confirmation
  const [adminInfo, setAdminInfo] = useState(null);

  // Fetch admin info on component mount
  useEffect(() => {
    const fetchAdminInfo = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        if (!token) return;

        const response = await api.get("/api/users/me", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setAdminInfo(response.data);
      } catch (error) {
        console.error("Failed to fetch admin info:", error);
        // Use fallback values if API call fails
        setAdminInfo({
          name: "Admin User",
          email: "admin@example.com",
        });
      }
    };

    fetchAdminInfo();
  }, []);

  const roleOptions = [
    { value: "director", label: "Sales Head" },
    { value: "zonal_head", label: "Zonal Head" },
    { value: "branch_manager", label: "Branch Manager" },
    { value: "team_lead", label: "Team Lead" },
    { value: "relationship_manager", label: "Relationship Manager" },
  ];

  // Ensure user lists shown in dropdowns never contain duplicates by id
  const uniqueById = (arr) => {
    const seen = new Set();
    return (arr || []).filter((item) => {
      const key = item?.id?.toString();
      if (!key) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const getRoleDisplayName = (roleValue) => {
    const roleMap = {
      team_lead: "Team Lead",
      branch_manager: "Branch Manager",
      zonal_head: "Zonal Head",
      director: "Director",
    };
    return roleMap[roleValue] || roleValue;
  };

  // Map UI section titles to grouped state keys
  const roleTitleToGroupKey = (title) => {
    switch (title) {
      case "Sales Heads":
        return "directors";
      case "Zonal Heads":
        return "zonalHeads";
      case "Branch Managers":
        return "branchManagers";
      case "Team Leads":
        return "teamLeads";
      case "Relationship Managers":
        return "relationshipManagers";
      default:
        return null;
    }
  };

  // Map backend role values to grouped state keys
  const roleValueToGroupKey = {
    director: "directors",
    zonal_head: "zonalHeads",
    branch_manager: "branchManagers",
    team_lead: "teamLeads",
    relationship_manager: "relationshipManagers",
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [
        usersRes,
        directorsRes,
        zonalHeadsRes,
        branchManagersRes,
        teamLeadsRes,
      ] = await Promise.all([
        api.get(`/api/admin/users/all?location=${locationFilter}`),
        api.get("/api/admin/directors"),
        api.get("/api/admin/zonal_heads"),
        api.get("/api/admin/branch_managers"),
        api.get("/api/admin/team_leads"),
      ]);

      // Group users by role
      const groupedUsers = usersRes.data.reduce(
        (acc, user) => {
          if (user.role === "director") acc.directors.push(user);
          else if (user.role === "zonal_head") acc.zonalHeads.push(user);
          else if (user.role === "branch_manager")
            acc.branchManagers.push(user);
          else if (user.role === "team_lead") acc.teamLeads.push(user);
          else if (user.role === "relationship_manager")
            acc.relationshipManagers.push(user);
          return acc;
        },
        {
          directors: [],
          zonalHeads: [],
          branchManagers: [],
          teamLeads: [],
          relationshipManagers: [],
        }
      );

      setUsers(groupedUsers);
      setDropdownData({
        directors: uniqueById(directorsRes.data),
        zonalHeads: uniqueById(zonalHeadsRes.data),
        branchManagers: uniqueById(branchManagersRes.data),
        teamLeads: uniqueById(teamLeadsRes.data),
      });
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to fetch data. Please refresh."
      );
    } finally {
      setLoading(false);
    }
  }, [locationFilter]);

  const roleToRoute = (role) => {
    const map = {
      "Sales Heads": "director",
      "Zonal Heads": "zonal_head",
      "Branch Managers": "branch_manager",
      "Team Leads": "team_lead",
      "Relationship Managers": "relationship_manager",
    };
    return map[role] || "";
  };

  const getProfileId = (user, role) => {
    // Return the correct profile ID based on role
    // CRITICAL: Never fall back to user.id as it could match a different user's profile ID!
    let profileId;
    switch (role) {
      case "Sales Heads":
        profileId = user.directorId;
        break;
      case "Zonal Heads":
        profileId = user.zonalId;
        break;
      case "Branch Managers":
        profileId = user.branchId;
        break;
      case "Team Leads":
        profileId = user.teamLeadId;
        break;
      case "Relationship Managers":
        profileId = user.rmId;
        break;
      default:
        profileId = user.id;
    }
    
    if (!profileId) {
      console.error(`Missing profile ID for ${role}:`, user);
      throw new Error(`Profile ID missing for ${role}. Cannot perform operation.`);
    }
    
    return profileId;
  };

  const isBulkSelectableRole = (title) =>
    BULK_ELIGIBLE_TITLES.includes(title);

  const toggleBulkUserSelection = (title, userId) => {
    if (!isBulkSelectableRole(title)) return;
    const numericId = Number(userId);
    if (Number.isNaN(numericId)) return;
    setSelectedBulkUsers((prev) => {
      const current = new Set(prev[title] || []);
      if (current.has(numericId)) current.delete(numericId);
      else current.add(numericId);
      return { ...prev, [title]: Array.from(current) };
    });
  };

  const toggleBulkSelectAll = (title, ids) => {
    if (!isBulkSelectableRole(title)) return;
    const normalizedIds = Array.from(
      new Set(
        ids
          .map((value) => Number(value))
          .filter((value) => !Number.isNaN(value))
      )
    );
    setSelectedBulkUsers((prev) => {
      const existing = prev[title] || [];
      const shouldClear =
        normalizedIds.length > 0 &&
        normalizedIds.every((id) => existing.includes(id));
      return {
        ...prev,
        [title]: shouldClear ? [] : normalizedIds,
      };
    });
  };

  const openBulkModal = (title) => {
    if (!title) return;
    setBulkModalState({
      roleTitle: title,
      supervisorUsername: "",
    });
    setShowBulkModal(true);
  };

  const closeBulkModal = () => {
    setShowBulkModal(false);
    setBulkModalState(createInitialBulkModalState());
  };

  const handleBulkUpdateSubmit = async () => {
    const roleTitle = bulkModalState.roleTitle;
    if (!roleTitle) return;
    const selectedIds = selectedBulkUsers[roleTitle] || [];
    const normalizedIds = selectedIds
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));
    if (!normalizedIds.length) {
      setError("Select at least one user to update.");
      return;
    }

    const supervisorUsername = bulkModalState.supervisorUsername?.trim();
    if (!supervisorUsername) {
      setError("Enter the supervisor username to continue.");
      return;
    }

    const subordinateRole = roleToRoute(roleTitle);
    if (!subordinateRole) {
      setError("Unable to determine subordinate role for bulk update.");
      return;
    }

    setBulkUpdating(true);
    try {
      await api.put("/api/admin/users/bulk-update-reports-to", {
        selectedUserIds: normalizedIds,
        subordinateRole,
        supervisorUsername,
      });
      setBulkUpdating(false);
      closeBulkModal();
      setSelectedBulkUsers((prev) => ({
        ...prev,
        [roleTitle]: [],
      }));
      fetchAllData();
    } catch (err) {
      setBulkUpdating(false);
      setError(
        err.response?.data?.message ||
          "Failed to update reports_to assignments."
      );
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    setSelectedBulkUsers((prev) => {
      let updated = prev;
      let hasChanges = false;

      BULK_ELIGIBLE_TITLES.forEach((title) => {
        const groupKey = roleTitleToGroupKey(title);
        const validUserIds = new Set(
          (groupKey ? users[groupKey] : [])?.map((user) => user.id) || []
        );
        const filtered = (prev[title] || []).filter((id) =>
          validUserIds.has(id)
        );
        if (filtered.length !== (prev[title] || []).length) {
          if (!hasChanges) {
            updated = { ...prev };
            hasChanges = true;
          }
          updated[title] = filtered;
        }
      });

      return hasChanges ? updated : prev;
    });
  }, [users]);

  // Show delete confirmation dialog
  const handleDelete = (user, role) => {
    setUserToDelete(user);
    setDeleteRole(role);
    setShowDeleteModal(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      const profileId = getProfileId(userToDelete, deleteRole);
      await api.delete(`/api/admin/${roleToRoute(deleteRole)}/${profileId}`);
      fetchAllData();
      setShowDeleteModal(false);
      setUserToDelete(null);
      setDeleteRole("");
    } catch (err) {
      // Handle both API errors and local errors (like missing profile ID)
      const errorMessage = err.response?.data?.message || err.message || "Failed to delete user";
      setError(errorMessage);
      setShowDeleteModal(false);
    }
  };

  // Show edit confirmation dialog
  const handleEdit = (user, role) => {
    setUserToEdit(user);
    setEditRole(role);
    setShowEditConfirmModal(true);
  };

  // Proceed with edit after confirmation
  const proceedWithEdit = () => {
    setShowEditConfirmModal(false);
    setEditingUser({ ...userToEdit, role: editRole });
    setFormData({
      name: userToEdit.name || "",
      username: userToEdit.username || "",
      email: userToEdit.email || "",
      location: userToEdit.location || "",
      // Don't pre-populate reports_to fields - make them optional for editing
      reports_to_role: null,
      reports_to_id: null,
    });

    // Initialize cascading dropdown state based on role
    if (editRole === "Relationship Managers") {
      setSelectedLocation("");
      setAvailableLocations([]);
      setAvailableUsers([]);
      setSelectedUser(null);
    } else if (editRole === "Team Leads") {
      setTlSelectedLocation("");
      setTlAvailableLocations([]);
      setTlAvailableUsers([]);
      setTlSelectedUser(null);
    } else if (editRole === "Branch Managers") {
      setBmSelectedLocation("");
      setBmAvailableLocations([]);
      setBmAvailableUsers([]);
      setBmSelectedUser(null);
    }

    setUserToEdit(null);
    setEditRole("");
  };

  const handleCancel = () => setEditingUser(null);
  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleEditRole = (user) => {
    setEditingRoleUser(user);
    setSelectedRole(user.role);
  };

  const handleCancelRoleEdit = () => {
    setEditingRoleUser(null);
    setSelectedRole("");
  };

  const handleSaveRole = async (e) => {
    // Prevent any default form submission behavior
    e?.preventDefault();
    e?.stopPropagation();

    if (!selectedRole || selectedRole === editingRoleUser.role) {
      setEditingRoleUser(null);
      setSelectedRole("");
      return;
    }

    try {
      // Get the current role route directly from the user's role
      const currentRoleRoute = editingRoleUser.role; // Use the role value directly (director, zonal_head, etc.)

      // For role changes, use the appropriate profile ID based on current role
      // CRITICAL: Do NOT fall back to user.id - this causes wrong user updates
      let profileId;
      switch (editingRoleUser.role) {
        case "director":
          profileId = editingRoleUser.directorId;
          break;
        case "zonal_head":
          profileId = editingRoleUser.zonalId;
          break;
        case "branch_manager":
          profileId = editingRoleUser.branchId;
          break;
        case "team_lead":
          profileId = editingRoleUser.teamLeadId;
          break;
        case "relationship_manager":
          profileId = editingRoleUser.rmId;
          break;
        default:
          profileId = null;
      }
      
      // Throw error if profile ID is missing - don't proceed with wrong ID
      if (!profileId) {
        throw new Error(`Profile ID missing for ${editingRoleUser.role}. Please refresh the page and try again.`);
      }

      // Persist role change and keep location unchanged in the backend
      await api.put(`/api/admin/${currentRoleRoute}/${profileId}`, {
        role: selectedRole,
        location: editingRoleUser.location,
      });

      // Refresh dropdown data and user list after role change
      await fetchAllData();

      // Also clear 'reports_to' for any subordinates that were reporting to this supervisor
      setUsers((prev) => {
        const pidStr = profileId?.toString();
        const clearSubordinates = (list, roleField, idField) =>
          (list || []).map((u) => {
            const r = u?.[roleField];
            const i = u?.[idField];
            if (
              r === editingRoleUser.role &&
              i != null &&
              i.toString() === pidStr
            ) {
              const cleared = { ...u, reports_to: null };
              cleared[roleField] = null;
              cleared[idField] = null;
              return cleared;
            }
            return u;
          });

        let updated = { ...prev };
        if (editingRoleUser.role === "team_lead") {
          updated.relationshipManagers = clearSubordinates(
            prev.relationshipManagers,
            "rmReportsToRole",
            "rmReportsToId"
          );
        } else if (editingRoleUser.role === "branch_manager") {
          updated.teamLeads = clearSubordinates(
            prev.teamLeads,
            "tlReportsToRole",
            "tlReportsToId"
          );
          updated.relationshipManagers = clearSubordinates(
            prev.relationshipManagers,
            "rmReportsToRole",
            "rmReportsToId"
          );
        } else if (editingRoleUser.role === "zonal_head") {
          updated.branchManagers = clearSubordinates(
            prev.branchManagers,
            "bmReportsToRole",
            "bmReportsToId"
          );
          updated.relationshipManagers = clearSubordinates(
            prev.relationshipManagers,
            "rmReportsToRole",
            "rmReportsToId"
          );
        } else if (editingRoleUser.role === "director") {
          updated.zonalHeads = clearSubordinates(
            prev.zonalHeads,
            "zhReportsToRole",
            "zhReportsToId"
          );
          updated.branchManagers = clearSubordinates(
            prev.branchManagers,
            "bmReportsToRole",
            "bmReportsToId"
          );
          updated.teamLeads = clearSubordinates(
            prev.teamLeads,
            "tlReportsToRole",
            "tlReportsToId"
          );
          updated.relationshipManagers = clearSubordinates(
            prev.relationshipManagers,
            "rmReportsToRole",
            "rmReportsToId"
          );
        }
        return updated;
      });

      setEditingRoleUser(null);
      setSelectedRole("");
    } catch (err) {
      console.error("Role change error:", err);
      setError(err.response?.data?.message || "Failed to update user role");
    }
  };

  const handleSave = async (e) => {
    // Prevent any default form submission behavior
    e?.preventDefault();
    e?.stopPropagation();

    // Validate that if reports_to_role is set, reports_to_id must also be set
    if (formData.reports_to_role && !formData.reports_to_id) {
      setError("Please complete the supervisor selection by choosing a specific supervisor from the dropdown.");
      return;
    }

    setSaving(true);
    setError(""); // Clear any previous errors

    try {
      let dataToSend = { ...formData };

      // Include admin information for confirmation email
      if (adminInfo) {
        dataToSend.emailConfirmation = adminInfo.email;
        dataToSend.adminName = adminInfo.name;
      }

      // Only include reports_to fields if they have been explicitly set
      if (!formData.reports_to_role) {
        delete dataToSend.reports_to_role;
        delete dataToSend.reports_to_id;
      }

      // Use the correct profile ID based on role
      const profileId = getProfileId(editingUser, editingUser.role);
      const apiUrl = `/api/admin/${roleToRoute(editingUser.role)}/${profileId}`;

      const response = await api.put(apiUrl, dataToSend);

      if (response.status >= 200 && response.status < 300) {

        // Optimistically update local state without refetch
        // Also set a human-readable `reports_to` for immediate UI reflection
        setUsers((prev) => {
          const groupKey = roleTitleToGroupKey(editingUser.role);
          if (!groupKey) {
            console.error("Failed to find groupKey for role:", editingUser.role);
            return prev;
          }

          // Compute the display name for reports_to when changed
          let reportsToDisplay = undefined;
          if (dataToSend.reports_to_role && dataToSend.reports_to_id) {
            const targetId = dataToSend.reports_to_id?.toString();
            const roleVal = dataToSend.reports_to_role;

            // Helper to safely extract name from an array
            const findName = (arr) =>
              (arr || []).find((x) => x.id?.toString() === targetId)?.name;

            // Prefer the explicitly selected user objects from state, then fallback lists
            if (editingUser.role === "Relationship Managers") {
              reportsToDisplay =
                selectedUser?.name ||
                (roleVal === "director"
                  ? findName(dropdownData.directors)
                  : findName(availableUsers));
            } else if (editingUser.role === "Team Leads") {
              reportsToDisplay =
                tlSelectedUser?.name ||
                (roleVal === "director"
                  ? findName(dropdownData.directors)
                  : findName(tlAvailableUsers));
            } else if (editingUser.role === "Branch Managers") {
              reportsToDisplay =
                bmSelectedUser?.name ||
                (roleVal === "director"
                  ? findName(dropdownData.directors)
                  : findName(bmAvailableUsers));
            } else if (editingUser.role === "Zonal Heads") {
              // Zonal Heads can only report to Director per UI
              reportsToDisplay =
                bmSelectedUser?.name || findName(dropdownData.directors);
            }
          }

          const updated = prev[groupKey].map((u) => {
            if (u.id !== editingUser.id) return u;
            const merged = { ...u, ...dataToSend };
            if (reportsToDisplay) merged.reports_to = reportsToDisplay;
            return merged;
          });

          return { ...prev, [groupKey]: updated };
        });

        setEditingUser(null);
        setFormData({}); // Clear form data
      } else {
        console.error("Unexpected response status:", response.status);
        setError(`Unexpected response: ${response.status}`);
      }
    } catch (err) {
      console.error("Error during save:", err);
      console.error("Error response:", err.response);
      console.error("Error status:", err.response?.status);
      console.error("Error message:", err.response?.data?.message);

      setError(err.response?.data?.message || err.message || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  // Functions for RM cascading dropdown
  const fetchLocationsForRole = async (role) => {
    try {
      const response = await api.get(`/api/admin/locations/${role}`);
      setAvailableLocations(response.data);
    } catch (err) {
      console.error("Error fetching locations:", err);
      setError("Failed to fetch locations");
    }
  };

  const fetchUsersForRoleAndLocation = async (role, location) => {
    try {
      const response = await api.get(
        `/api/admin/users?role=${role}&location=${location}`
      );
      setAvailableUsers(uniqueById(response.data));
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to fetch users");
    }
  };

  // Functions for Team Lead cascading dropdown
  const fetchTlLocationsForRole = async (role) => {
    try {
      const response = await api.get(`/api/admin/locations/${role}`);
      setTlAvailableLocations(response.data);
    } catch (err) {
      console.error("Error fetching TL locations:", err);
      setError("Failed to fetch locations");
    }
  };

  const fetchTlUsersForRoleAndLocation = async (role, location) => {
    try {
      const response = await api.get(
        `/api/admin/users?role=${role}&location=${location}`
      );
      setTlAvailableUsers(uniqueById(response.data));
    } catch (err) {
      console.error("Error fetching TL users:", err);
      setError("Failed to fetch users");
    }
  };

  // Functions for Branch Manager cascading dropdown
  const fetchBmLocationsForRole = async (role) => {
    try {
      const response = await api.get(`/api/admin/locations/${role}`);
      setBmAvailableLocations(response.data);
    } catch (err) {
      console.error("Error fetching BM locations:", err);
      setError("Failed to fetch locations");
    }
  };

  const fetchBmUsersForRoleAndLocation = async (role, location) => {
    try {
      const response = await api.get(
        `/api/admin/users?role=${role}&location=${location}`
      );
      setBmAvailableUsers(uniqueById(response.data));
    } catch (err) {
      console.error("Error fetching BM users:", err);
      setError("Failed to fetch users");
    }
  };

  // Effect to fetch locations when role changes
  useEffect(() => {
    if (
      formData.reports_to_role &&
      editingUser?.role === "Relationship Managers"
    ) {
      if (formData.reports_to_role === "director") {
        // For directors, set "All Locations" and fetch users directly
        setAvailableLocations([{ value: "all", label: "All Locations" }]);
        setSelectedLocation("all");
        // Fetch users immediately for directors
        fetchUsersForRoleAndLocation("director", "all");
      } else {
        setSelectedLocation("");
        setAvailableUsers([]);
        fetchLocationsForRole(formData.reports_to_role);
      }
    } else {
      // Clear when no role selected
      setAvailableLocations([]);
      setSelectedLocation("");
      setAvailableUsers([]);
    }
  }, [formData.reports_to_role, editingUser]);

  // Effect to fetch users when location changes (for non-director roles)
  useEffect(() => {
    if (
      selectedLocation &&
      formData.reports_to_role &&
      formData.reports_to_role !== "director" &&
      editingUser?.role === "Relationship Managers"
    ) {
      fetchUsersForRoleAndLocation(formData.reports_to_role, selectedLocation);
    }
  }, [selectedLocation, formData.reports_to_role, editingUser]);

  // Effect to fetch TL locations when role changes
  useEffect(() => {
    if (formData.reports_to_role && editingUser?.role === "Team Leads") {
      if (formData.reports_to_role === "director") {
        // For directors, set "All Locations" and fetch users directly
        setTlAvailableLocations([{ value: "all", label: "All Locations" }]);
        setTlSelectedLocation("all");
        // Fetch users immediately for directors
        fetchTlUsersForRoleAndLocation("director", "all");
      } else {
        setTlSelectedLocation("");
        setTlAvailableUsers([]);
        fetchTlLocationsForRole(formData.reports_to_role);
      }
    } else {
      // Clear when no role selected
      setTlAvailableLocations([]);
      setTlSelectedLocation("");
      setTlAvailableUsers([]);
    }
  }, [formData.reports_to_role, editingUser]);

  // Effect to fetch TL users when location changes (for non-director roles)
  useEffect(() => {
    if (
      tlSelectedLocation &&
      formData.reports_to_role &&
      formData.reports_to_role !== "director" &&
      editingUser?.role === "Team Leads"
    ) {
      fetchTlUsersForRoleAndLocation(
        formData.reports_to_role,
        tlSelectedLocation
      );
    }
  }, [tlSelectedLocation, formData.reports_to_role, editingUser]);

  // Effect to fetch BM locations when role changes
  useEffect(() => {
    if (formData.reports_to_role && editingUser?.role === "Branch Managers") {
      if (formData.reports_to_role === "director") {
        // For directors, set "All Locations" and fetch users directly
        setBmAvailableLocations([{ value: "all", label: "All Locations" }]);
        setBmSelectedLocation("all");
        // Fetch users immediately for directors
        fetchBmUsersForRoleAndLocation("director", "all");
      } else {
        setBmSelectedLocation("");
        setBmAvailableUsers([]);
        fetchBmLocationsForRole(formData.reports_to_role);
      }
    } else {
      // Clear when no role selected
      setBmAvailableLocations([]);
      setBmSelectedLocation("");
      setBmAvailableUsers([]);
    }
  }, [formData.reports_to_role, editingUser]);

  // Effect to fetch BM users when location changes (for non-director roles)
  useEffect(() => {
    if (
      bmSelectedLocation &&
      formData.reports_to_role &&
      formData.reports_to_role !== "director" &&
      editingUser?.role === "Branch Managers"
    ) {
      fetchBmUsersForRoleAndLocation(
        formData.reports_to_role,
        bmSelectedLocation
      );
    }
  }, [bmSelectedLocation, formData.reports_to_role, editingUser]);

  const renderUserList = (title, userList) => {
    const isBulkSelectable = isBulkSelectableRole(title);
    const selectedIdsForRole = selectedBulkUsers[title] || [];
    const userIds = userList.map((user) => user.id);
    const isAllSelected =
      isBulkSelectable &&
      userIds.length > 0 &&
      userIds.every((id) => selectedIdsForRole.includes(id));

    const renderRow = (user) => {
      const isEditing =
        editingUser?.id === user.id && editingUser?.role === title;
      const tds = [];

       if (isBulkSelectable) {
        const isChecked = selectedIdsForRole.includes(user.id);
        tds.push(
          <td key="select" className="text-center">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={() => toggleBulkUserSelection(title, user.id)}
              disabled={isEditing}
              aria-label={`Select ${user.name} for bulk update`}
            />
          </td>
        );
      }

      // ID column
      tds.push(<td key="id">{user.id}</td>);

      // Name column
      tds.push(
        <td key="name">
          {isEditing ? (
            <TextField
              name="name"
              value={formData.name}
              onChange={handleChange}
              variant="standard"
              fullWidth
            />
          ) : (
            user.name
          )}
        </td>
      );

      // Username column
      tds.push(
        <td key="username">
          {isEditing ? (
            <TextField
              name="username"
              value={formData.username}
              onChange={handleChange}
              variant="standard"
              fullWidth
            />
          ) : (
            user.username
          )}
        </td>
      );

      // Email column
      tds.push(
        <td key="email">
          {isEditing ? (
            <TextField
              name="email"
              value={formData.email}
              onChange={handleChange}
              variant="standard"
              fullWidth
            />
          ) : (
            user.email || "N/A"
          )}
        </td>
      );

      // Location column
      tds.push(
        <td key="location">
          {isEditing ? (
            <select
              name="location"
              value={formData.location || ""}
              onChange={handleChange}
              style={{
                padding: "4px 8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "14px",
                width: "100%",
                minWidth: "120px",
              }}
            >
              <option value="">Select Location</option>
              <option value="Chennai">Chennai</option>
              <option value="Trichy">Trichy</option>
              <option value="Coimbatore">Coimbatore</option>
              <option value="Bangalore">Bangalore</option>
              <option value="Dubai">Dubai</option>
              <option value="Singapore">Singapore</option>
              <option value="Madurai">Madurai</option>
            </select>
          ) : (
            user.location || "N/A"
          )}
        </td>
      );

      // Role column
      tds.push(
        <td key="role">
          {editingRoleUser?.id === user.id ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <select
                value={selectedRole}
                onChange={(e) => handleSelectChange(e, setSelectedRole)}
                style={{
                  padding: "4px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <Button
                variant="link"
                className="text-success"
                onClick={handleSaveRole}
                type="button"
                style={{ padding: "0", margin: "0" }}
              >
                <FiSave size={16} />
              </Button>
              <Button
                variant="link"
                className="text-secondary"
                onClick={handleCancelRoleEdit}
                type="button"
                style={{ padding: "0", margin: "0" }}
              >
                <FiXCircle size={16} />
              </Button>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>
                {roleOptions.find((opt) => opt.value === user.role)?.label ||
                  user.role}
              </span>
              <Button
                variant="link"
                className="text-warning"
                onClick={() => handleEditRole(user)}
                type="button"
                style={{
                  padding: "0",
                  margin: "0 0 0 8px",
                }}
                title="Edit Role"
              >
                <FiEdit2 size={14} />
              </Button>
            </div>
          )}
        </td>
      );

      // Reports To column (conditional)
      if (title !== "Sales Heads") {
        tds.push(
          <td key="reports_to">
            {!isEditing && (user.reports_to || "N/A")}
            {title === "Zonal Heads" && isEditing && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  minWidth: "250px",
                }}
              >
                {/* Reports To Role */}
                <div>
                  <label
                    style={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: "#666",
                      marginBottom: "2px",
                      display: "block",
                    }}
                  >
                    Reports To Role (for Relationship Manager):
                  </label>
                  <select
                    value={formData.reports_to_role || ""}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newRole = e.target.value;
                      setFormData({
                        ...formData,
                        reports_to_role: newRole,
                        reports_to_id: "",
                      });
                      // Clear location and users when role changes
                      setBmSelectedLocation("");
                      setBmAvailableUsers([]);
                      setBmSelectedUser(null);
                    }}
                    style={{
                      padding: "4px 8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "14px",
                      width: "100%",
                    }}
                  >
                    <option value="">Select Role</option>
                    <option value="director">Director</option>
                  </select>
                </div>

                {/* User Selection */}
                {formData.reports_to_role === "director" && (
                  <div>
                    <label
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "#666",
                        marginBottom: "2px",
                        display: "block",
                      }}
                    >
                      Select Supervisor:
                    </label>
                    <select
                      value={formData.reports_to_id || ""}
                      onChange={(e) => {
                        const userId = e.target.value;
                        const selectedUser = dropdownData.directors.find(
                          (u) => u.id.toString() === userId
                        );
                        setBmSelectedUser(selectedUser);
                        setFormData({
                          ...formData,
                          reports_to_id: userId,
                        });
                      }}
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        fontSize: "14px",
                        width: "100%",
                      }}
                      disabled={dropdownData.directors.length === 0}
                    >
                      <option value="">Select Supervisor</option>
                      {dropdownData.directors.map((user, index) => (
                        <option
                          key={`bm-director-${user.id}-${index}`}
                          value={user.id}
                        >
                          {user.name} (Director - All Locations)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Display selected user */}
                {bmSelectedUser && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    Selected: {bmSelectedUser.name} (Director - All Locations)
                  </div>
                )}
              </div>
            )}
            {title === "Team Leads" && isEditing && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  minWidth: "250px",
                }}
              >
                {/* Reports To Role */}
                <div>
                  <label
                    style={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: "#666",
                      marginBottom: "2px",
                      display: "block",
                    }}
                  >
                    Reports To Role (for Team Lead):
                  </label>
                  <select
                    value={formData.reports_to_role || ""}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newRole = e.target.value;
                      setFormData({
                        ...formData,
                        reports_to_role: newRole,
                        reports_to_id: "",
                      });
                      // Clear location and users when role changes
                      setTlSelectedLocation("");
                      setTlAvailableUsers([]);
                      setTlSelectedUser(null);
                    }}
                    style={{
                      padding: "4px 8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "14px",
                      width: "100%",
                    }}
                  >
                    <option value="">Select Role</option>
                    <option value="branch_manager">Branch Manager</option>
                    <option value="zonal_head">Zonal Head</option>
                    <option value="director">Director</option>
                  </select>
                </div>

                {/* Location Selection - only show if not Director */}
                {formData.reports_to_role &&
                  formData.reports_to_role !== "director" && (
                    <div>
                      <label
                        style={{
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: "#666",
                          marginBottom: "2px",
                          display: "block",
                        }}
                      >
                        Location:
                      </label>
                      <select
                        value={tlSelectedLocation}
                        onChange={(e) => {
                          setTlSelectedLocation(e.target.value);
                          setTlAvailableUsers([]);
                          setTlSelectedUser(null);
                          setFormData({
                            ...formData,
                            reports_to_id: "",
                          });
                        }}
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          fontSize: "14px",
                          width: "100%",
                        }}
                        disabled={!formData.reports_to_role}
                      >
                        <option value="">Select Location</option>
                        {tlAvailableLocations.map((location) => (
                          <option key={location.value} value={location.value}>
                            {location.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                {/* User Selection */}
                {((formData.reports_to_role && tlSelectedLocation) ||
                  formData.reports_to_role === "director") && (
                  <div>
                    <label
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "#666",
                        marginBottom: "2px",
                        display: "block",
                      }}
                    >
                      Select Supervisor:
                    </label>
                    <select
                      value={formData.reports_to_id || ""}
                      onChange={(e) => {
                        const userId = e.target.value;
                        const selectedUser = tlAvailableUsers.find(
                          (u) => u.id.toString() === userId
                        );
                        setTlSelectedUser(selectedUser);
                        setFormData({
                          ...formData,
                          reports_to_id: userId,
                        });
                      }}
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        fontSize: "14px",
                        width: "100%",
                      }}
                      disabled={tlAvailableUsers.length === 0}
                    >
                      <option value="">Select Supervisor</option>
                      {tlAvailableUsers.map((user, index) => (
                        <option key={`tl-${user.id}-${index}`} value={user.id}>
                          {user.name} (
                          {getRoleDisplayName(formData.reports_to_role)} -{" "}
                          {tlSelectedLocation})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Display selected user */}
                {tlSelectedUser && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    Selected: {tlSelectedUser.name} (
                    {getRoleDisplayName(formData.reports_to_role)} -{" "}
                    {tlSelectedLocation})
                  </div>
                )}
              </div>
            )}
            {title === "Branch Managers" && isEditing && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  minWidth: "250px",
                }}
              >
                {/* Reports To Role */}
                <div>
                  <label
                    style={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: "#666",
                      marginBottom: "2px",
                      display: "block",
                    }}
                  >
                    Reports To Role (for Branch Manager):
                  </label>
                  <select
                    value={formData.reports_to_role || ""}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newRole = e.target.value;
                      setFormData({
                        ...formData,
                        reports_to_role: newRole,
                        reports_to_id: "",
                      });
                      // Clear location and users when role changes
                      setBmSelectedLocation("");
                      setBmAvailableUsers([]);
                      setBmSelectedUser(null);
                    }}
                    style={{
                      padding: "4px 8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "14px",
                      width: "100%",
                    }}
                  >
                    <option value="">Select Role</option>
                    <option value="zonal_head">Zonal Head</option>
                    <option value="director">Director</option>
                  </select>
                </div>

                {/* Location Selection - only show if not Director */}
                {formData.reports_to_role &&
                  formData.reports_to_role !== "director" && (
                    <div>
                      <label
                        style={{
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: "#666",
                          marginBottom: "2px",
                          display: "block",
                        }}
                      >
                        Location:
                      </label>
                      <select
                        value={bmSelectedLocation}
                        onChange={(e) => {
                          setBmSelectedLocation(e.target.value);
                          setBmAvailableUsers([]);
                          setBmSelectedUser(null);
                          setFormData({
                            ...formData,
                            reports_to_id: "",
                          });
                        }}
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          fontSize: "14px",
                          width: "100%",
                        }}
                        disabled={!formData.reports_to_role}
                      >
                        <option value="">Select Location</option>
                        {bmAvailableLocations.map((location) => (
                          <option key={location.value} value={location.value}>
                            {location.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                {/* User Selection */}
                {((formData.reports_to_role && bmSelectedLocation) ||
                  formData.reports_to_role === "director") && (
                  <div>
                    <label
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "#666",
                        marginBottom: "2px",
                        display: "block",
                      }}
                    >
                      Select Supervisor:
                    </label>
                    <select
                      value={formData.reports_to_id || ""}
                      onChange={(e) => {
                        const userId = e.target.value;
                        const selectedUser = bmAvailableUsers.find(
                          (u) => u.id.toString() === userId
                        );
                        setBmSelectedUser(selectedUser);
                        setFormData({
                          ...formData,
                          reports_to_id: userId,
                        });
                      }}
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        fontSize: "14px",
                        width: "100%",
                      }}
                      disabled={bmAvailableUsers.length === 0}
                    >
                      <option value="">Select Supervisor</option>
                      {bmAvailableUsers.map((user, index) => (
                        <option key={`bm-${user.id}-${index}`} value={user.id}>
                          {user.name} (
                          {getRoleDisplayName(formData.reports_to_role)} -{" "}
                          {bmSelectedLocation})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Display selected user */}
                {bmSelectedUser && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    Selected: {bmSelectedUser.name} (
                    {getRoleDisplayName(formData.reports_to_role)} -{" "}
                    {bmSelectedLocation})
                  </div>
                )}
              </div>
            )}
            {title === "Relationship Managers" && isEditing && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  minWidth: "250px",
                }}
              >
                {/* Reports To Role */}
                <div>
                  <label
                    style={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: "#666",
                      marginBottom: "2px",
                      display: "block",
                    }}
                  >
                    Reports To Role (for Relationship Manager):
                  </label>
                  <select
                    value={formData.reports_to_role || ""}
                    onChange={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newRole = e.target.value;
                      setFormData({
                        ...formData,
                        reports_to_role: newRole,
                        reports_to_id: "",
                      });
                      // Clear location and users when role changes
                      setSelectedLocation("");
                      setAvailableUsers([]);
                      setSelectedUser(null);
                    }}
                    style={{
                      padding: "4px 8px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "14px",
                      width: "100%",
                    }}
                  >
                    <option value="">Select Role</option>
                    <option value="team_lead">Team Lead</option>
                    <option value="branch_manager">Branch Manager</option>
                    <option value="zonal_head">Zonal Head</option>
                    <option value="director">Director</option>
                  </select>
                </div>

                {/* Location Selection - only show if not Director */}
                {formData.reports_to_role &&
                  formData.reports_to_role !== "director" && (
                    <div>
                      <label
                        style={{
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: "#666",
                          marginBottom: "2px",
                          display: "block",
                        }}
                      >
                        Location:
                      </label>
                      <select
                        value={selectedLocation}
                        onChange={(e) => {
                          setSelectedLocation(e.target.value);
                          setAvailableUsers([]);
                          setSelectedUser(null);
                          setFormData({
                            ...formData,
                            reports_to_id: "",
                          });
                        }}
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          fontSize: "14px",
                          width: "100%",
                        }}
                        disabled={!formData.reports_to_role}
                      >
                        <option value="">Select Location</option>
                        {availableLocations.map((location) => (
                          <option key={location.value} value={location.value}>
                            {location.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                {/* User Selection */}
                {((formData.reports_to_role && selectedLocation) ||
                  formData.reports_to_role === "director") && (
                  <div>
                    <label
                      style={{
                        fontSize: "12px",
                        fontWeight: "bold",
                        color: "#666",
                        marginBottom: "2px",
                        display: "block",
                      }}
                    >
                      Select Supervisor:
                    </label>
                    <select
                      value={formData.reports_to_id || ""}
                      onChange={(e) => {
                        const userId = e.target.value;
                        const selectedUser = availableUsers.find(
                          (u) => u.id.toString() === userId
                        );
                        setSelectedUser(selectedUser);
                        setFormData({
                          ...formData,
                          reports_to_id: userId,
                        });
                      }}
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        fontSize: "14px",
                        width: "100%",
                      }}
                      disabled={availableUsers.length === 0}
                    >
                      <option value="">Select Supervisor</option>
                      {availableUsers.map((user, index) => (
                        <option key={`rm-${user.id}-${index}`} value={user.id}>
                          {user.name} (
                          {getRoleDisplayName(formData.reports_to_role)} -{" "}
                          {selectedLocation})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Display selected user */}
                {selectedUser && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    Selected: {selectedUser.name} (
                    {getRoleDisplayName(formData.reports_to_role)} -{" "}
                    {selectedLocation})
                  </div>
                )}
              </div>
            )}
          </td>
        );
      }

      // Actions column
      tds.push(
        <td key="actions" className="text-center">
          {isEditing ? (
            <div className="action-buttons">
              <Button
                variant="link"
                className="text-success"
                onClick={handleSave}
                type="button"
                disabled={saving}
              >
                {saving ? <Spinner size="sm" /> : <FiSave size={18} />}
              </Button>
              <Button
                variant="link"
                className="text-secondary"
                onClick={handleCancel}
                type="button"
                disabled={saving}
              >
                <FiXCircle size={18} />
              </Button>
            </div>
          ) : (
            <div className="action-buttons">
              <Button
                variant="link"
                className="text-primary"
                onClick={() => handleEdit(user, title)}
                type="button"
              >
                <FiEdit2 size={16} />
              </Button>
              <Button
                variant="link"
                className="text-danger"
                onClick={() => handleDelete(user, title)}
                type="button"
              >
                <FiTrash2 size={16} />
              </Button>
            </div>
          )}
        </td>
      );

      return tds;
    };

    return (
      <div className="user-list-card">
        <h3 className="user-list-title">{title}</h3>
        {isBulkSelectable && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              marginBottom: "12px",
              gap: "8px",
              fontSize: "0.9rem",
              color: "#555",
            }}
          >
            <span>{selectedIdsForRole.length} selected</span>
          </div>
        )}
        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="manage-users-table">
              <thead>
                <tr>
                  {isBulkSelectable && (
                    <th className="text-center">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={() => toggleBulkSelectAll(title, userIds)}
                        aria-label={`Select all ${title}`}
                      />
                    </th>
                  )}
                  <th>ID</th>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Location</th>
                  <th>Role</th>
                  {title !== "Sales Heads" && <th>Reports To</th>}
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {userList.map((user) => (
                  <tr key={`${title}-${user.id}`}>{renderRow(user)}</tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    );
  };

  const handleSelectChange = (e, setter) => {
    e.preventDefault();
    e.stopPropagation();
    setter(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const totalSelectedCount = BULK_ELIGIBLE_TITLES.reduce(
    (sum, title) => sum + (selectedBulkUsers[title]?.length || 0),
    0
  );
  const rolesWithSelection = BULK_ELIGIBLE_TITLES.filter(
    (title) => (selectedBulkUsers[title] || []).length > 0
  );
  const activeBulkRoleTitle = rolesWithSelection[0] || "";
  const multipleRoleSelection = rolesWithSelection.length > 1;
  const bulkModalSelectedCount = bulkModalState.roleTitle
    ? selectedBulkUsers[bulkModalState.roleTitle]?.length || 0
    : 0;
  const disableGlobalAssign =
    multipleRoleSelection || totalSelectedCount === 0;

  return (
    <div className="manage-users-page">
      <Container>
        <div className="page-header">
          <h2 className="page-title">
            <FiUsers /> Manage Users
          </h2>
          <Button
            variant="outline-primary"
            className="back-button"
            onClick={() => navigate("/admin-dashboard")}
            type="button"
          >
            <FiArrowLeft /> Back to Dashboard
          </Button>
        </div>

        <div className="filter-panel">
          <label htmlFor="locationFilter">Filter by Location</label>
          <select
            id="locationFilter"
            value={locationFilter}
            onChange={(e) => handleSelectChange(e, setLocationFilter)}
          >
            <option value="all">All Locations</option>
            <option value="Chennai">Chennai</option>
            <option value="Trichy">Trichy</option>
            <option value="Coimbatore">Coimbatore</option>
            <option value="Bangalore">Bangalore</option>
            <option value="Dubai">Dubai</option>
            <option value="Singapore">Singapore</option>
            <option value="Madurai">Madurai</option>
          </select>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "10px",
          }}
        >
          <Button
            variant="outline-secondary"
            size="sm"
            type="button"
            onClick={() => navigate("/deleted-users")}
          >
            View Deleted Users
          </Button>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
        >
          <div style={{ fontSize: "0.95rem", color: "#333" }}>
            Selected Users: {totalSelectedCount}
            {multipleRoleSelection &&
              " (select from only one role group to proceed)"}
          </div>
          <Button
            variant="primary"
            type="button"
            disabled={disableGlobalAssign || bulkUpdating}
            onClick={() => openBulkModal(activeBulkRoleTitle)}
          >
            Assign Reports To
          </Button>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {renderUserList("Sales Heads", users.directors)}
        {renderUserList("Zonal Heads", users.zonalHeads)}
        {renderUserList("Branch Managers", users.branchManagers)}
        {renderUserList("Team Leads", users.teamLeads)}
        {renderUserList("Relationship Managers", users.relationshipManagers)}

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete {userToDelete?.name}? This action
            cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Edit Confirmation Modal */}
        <Modal
          show={showEditConfirmModal}
          onHide={() => setShowEditConfirmModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Edit</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to edit {userToEdit?.name}?</p>
            <p>
              This will allow you to modify their information in the system.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowEditConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={proceedWithEdit}>
              Proceed to Edit
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Bulk Reports To Modal */}
        <Modal show={showBulkModal} onHide={closeBulkModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              Assign Reports To{" "}
              {bulkModalState.roleTitle ? `(${bulkModalState.roleTitle})` : ""}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ fontSize: "0.9rem", color: "#555" }}>
              {bulkModalSelectedCount} user
              {bulkModalSelectedCount === 1 ? "" : "s"} selected. Provide the
              username of the supervisor, and the same reports-to will be
              applied to every selected user.
            </p>

            <TextField
              fullWidth
              label="Supervisor Username"
              value={bulkModalState.supervisorUsername}
              onChange={(e) =>
                setBulkModalState((prev) => ({
                  ...prev,
                  supervisorUsername: e.target.value,
                }))
              }
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeBulkModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleBulkUpdateSubmit}
              disabled={
                bulkUpdating ||
                !bulkModalSelectedCount ||
                !bulkModalState.supervisorUsername?.trim()
              }
            >
              {bulkUpdating
                ? "Updating..."
                : `Update ${bulkModalSelectedCount} ${
                    bulkModalSelectedCount === 1 ? "User" : "Users"
                  }`}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default ManageUsers;
